import { useCallback, useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import { subscribeConnectivity } from '../../shell/connectivity/connectivityInfra';
import { invokePendingFiscalSideEffects } from '../core/manualSync';
import { isFirstCatalogSyncNeeded, MASTERS_BACKUP_INTERVAL_MS, getLocalCatalogProductsCount, shouldAutoPullCatalog } from '../core/masterSyncPolicy';
import { refreshRemoteDomains } from '../core/refreshRemoteDomains';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { canRunOperationalBackgroundWork } from '../../shell/session/accountSetupService';
import { shouldAttemptSessionReadyPull } from './sessionReadyPullGate';

type SyncReason =
  | 'startup'
  | 'startup-retry'
  | 'session-ready'
  | 'network'
  | 'timer'
  | 'remote-timer'
  | 'focus';

function isAppVisibleForMasterSync(): boolean {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return document.visibilityState === 'visible';
  }
  return AppState.currentState === 'active';
}

export const useSyncCoordinator = (opts?: { operationalReady?: boolean }) => {
  const pushInFlightRef = useRef(false);
  const sessionPullAttemptedRef = useRef(false);
  const pendingSessionReadyRef = useRef(false);
  const appVisibleRef = useRef(isAppVisibleForMasterSync());
  const userId = useAuthStore((s) => s.session?.user?.id);
  const operationalReady = opts?.operationalReady === true;

  const runRemoteRefresh = useCallback(async (opts?: { force?: boolean; forceCatalog?: boolean; includeCustomer?: boolean }) => {
    if (!isAppVisibleForMasterSync()) return;
    if (!canRunOperationalBackgroundWork()) return;
    try {
      const localCount = await getLocalCatalogProductsCount();
      const forceCatalog = opts?.forceCatalog === true || localCount === 0;
      await refreshRemoteDomains({ ...opts, forceCatalog });
    } catch (err) {
      console.warn('[SyncCoordinator] refreshRemoteDomains', err);
    }
  }, []);

  const syncNow = useCallback(
    async (reason: SyncReason = 'startup') => {
      if (pushInFlightRef.current) {
        if (reason === 'session-ready') {
          pendingSessionReadyRef.current = true;
        }
        return;
      }
      pushInFlightRef.current = true;
      const backgroundSyncAllowed = canRunOperationalBackgroundWork();
      const skipFiscalOnStartup =
        !backgroundSyncAllowed ||
        reason === 'startup' ||
        reason === 'startup-retry' ||
        reason === 'session-ready';
      try {
        await invokePendingFiscalSideEffects({ skipFiscal: skipFiscalOnStartup });

        const { session } = useAuthStore.getState();
        if (session && backgroundSyncAllowed && reason === 'session-ready') {
          const firstSync = await isFirstCatalogSyncNeeded();
          const localProductsCount = await getLocalCatalogProductsCount();
          const pullCatalog = await shouldAutoPullCatalog({
            forceCatalog: firstSync,
            localProductsCount,
          });
          if (pullCatalog) {
            await runRemoteRefresh({
              forceCatalog: firstSync || localProductsCount === 0,
            });
          }
        } else if (
          session &&
          backgroundSyncAllowed &&
          (reason === 'network' || reason === 'timer' || reason === 'focus')
        ) {
          const localProductsCount = await getLocalCatalogProductsCount();
          if (await shouldAutoPullCatalog({ localProductsCount })) {
            await runRemoteRefresh({
              forceCatalog: localProductsCount === 0,
            });
          }
        }
      } catch (err) {
        console.warn('[SyncCoordinator]', reason, err);
        if (reason === 'startup') {
          setTimeout(() => {
            pushInFlightRef.current = false;
            void syncNow('startup-retry');
          }, 3000);
          return;
        }
      } finally {
        pushInFlightRef.current = false;
        if (pendingSessionReadyRef.current) {
          pendingSessionReadyRef.current = false;
          void syncNow('session-ready');
        }
      }
    },
    [runRemoteRefresh]
  );

  useEffect(() => {
    void syncNow('startup');

    const unsubscribe = subscribeConnectivity((status) => {
      if (status.isOnline) {
        void syncNow('network');
      }
    });

    const remotePullTimer = setInterval(async () => {
      if (!appVisibleRef.current) return;
      try {
        if (!useAuthStore.getState().session) return;
        await runRemoteRefresh();
      } catch (error) {
        console.warn('[SyncCoordinator] remote pull timer', error);
      }
    }, MASTERS_BACKUP_INTERVAL_MS);

    const appStateSub = AppState.addEventListener('change', (state) => {
      appVisibleRef.current = state === 'active';
      if (state === 'active') {
        void runRemoteRefresh();
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        appVisibleRef.current = document.visibilityState === 'visible';
        if (document.visibilityState === 'visible') {
          void runRemoteRefresh();
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      appVisibleRef.current = document.visibilityState === 'visible';
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      unsubscribe();
      clearInterval(remotePullTimer);
      appStateSub.remove();
      removeVisibility?.();
    };
  }, [syncNow, runRemoteRefresh]);

  useEffect(() => {
    if (!userId) {
      sessionPullAttemptedRef.current = false;
      pendingSessionReadyRef.current = false;
      return;
    }
    if (
      !shouldAttemptSessionReadyPull({
        hasUserId: true,
        operationalReady,
        alreadyAttempted: sessionPullAttemptedRef.current,
      })
    ) {
      return;
    }
    sessionPullAttemptedRef.current = true;
    void syncNow('session-ready');
  }, [userId, operationalReady, syncNow]);

  return { syncNow, refreshRemoteDomains: runRemoteRefresh };
};
